import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { registerSW } from 'virtual:pwa-register';

import store from './state/store';
import '@fontsource/noto-sans-thai/400.css';
import '@fontsource/noto-sans-thai/700.css';
import './index.css'; // TODO: This needs types or sth
import App from './App';
import { asyncPersister, shouldPersistQuery } from './offline/persister';
import { followServerReachability, queryClient } from './queryClient';
import { initI18n } from './i18n';
import { deployedBuild, keepCurrent } from './offline/releaseCheck';

// Note: The below is for Tanstack Query Dev Tools to work and it doesn't emit any javascript, so it doesn't need any 'isDevelopment' guards:
declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__:
    import('@tanstack/query-core')
    .QueryClient
  }
}

if (import.meta.env.DEV) {
  window.__TANSTACK_QUERY_CLIENT__ = queryClient
}

console.info(`Eclipse frontend build ${__BUILD_ID__}`);
(window as unknown as { __ECLIPSE_BUILD__: string }).__ECLIPSE_BUILD__ = __BUILD_ID__;

// Note: After a new release, and even though data is reloaded and cached when logging in, the old bundle was causing some lists to be empty,
// for example: the material drop-down in the loading page
registerSW({
  immediate: true,
  // Note: a page loaded from the network is usually already the release the new worker brings, so it reloads only when not.
  onNeedReload: () => void deployedBuild().then(deployed => {
    if (deployed !== __BUILD_ID__) window.location.reload();
  }),
});
if (import.meta.env.PROD) keepCurrent(__BUILD_ID__);
followServerReachability();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
const render = () => root.render(
  <StrictMode>
    <Provider store={store}>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister: asyncPersister,
          maxAge: 1000 * 60 * 60 * 24 * 7,
          // Note: A new build starts from an empty persisted cache, so data shaped by the previous release is
          // never restored into the new one.
          buster: __BUILD_ID__,
          dehydrateOptions: { shouldDehydrateQuery: shouldPersistQuery },
        }}
      >
        <App />
      </PersistQueryClientProvider>
    </Provider>
  </StrictMode>
);

initI18n().then(render, render);
